import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

/**
 * Talking to the to-do templates (routes/todo-templates.js, migration 071),
 * and the small sums the screens around them share.
 *
 * Staff only: a contractor's token is a 403 on every route, so nothing here
 * carries one - the Entra token goes with every call through the
 * interceptor in lib/apiClient.js. Until 071 has run every call answers 503
 * naming the migration; `notReady` recognises that.
 *
 * A template's steps count days from the date it is applied with - working
 * days (weekends and Malta's public holidays skipped) or calendar days -
 * forwards from a start date or back from a due date. The dates themselves
 * are worked out by the server (lib/malta-days.js) and shown in the Apply
 * dialog's preview; the editor only ever says "3 working days before".
 */

const URL = `${API_BASE}/todo-templates`;
const data = (r) => r.data;

export const notReady = (err) => err?.response?.status === 503;

export const loadTemplates = ({ deleted = false } = {}) => axios
  .get(URL, deleted ? { params: { deleted: 1 } } : undefined).then(data);
export const loadTemplate = (id) => axios.get(`${URL}/${id}`).then(data);
export const createTemplate = (body) => axios.post(URL, body).then(data);
export const saveTemplate = (id, body) => axios.put(`${URL}/${id}`, body).then(data);
export const duplicateTemplate = (id) => axios.post(`${URL}/${id}/duplicate`, {}).then(data);
export const deleteTemplate = (id) => axios.delete(`${URL}/${id}`).then(data);
export const restoreTemplate = (id) => axios.post(`${URL}/${id}/restore`, {}).then(data);
export const previewTemplate = (id, body) => axios.post(`${URL}/${id}/preview`, body).then(data);
export const applyTemplate = (id, body) => axios.post(`${URL}/${id}/apply`, body).then(data);
export const draftFromList = (listId) => axios.get(`${URL}/from-list/${listId}`).then(data);
export const applyOptions = () => axios.get(`${URL}/options`).then(data);

/** The words for an error from any of these. */
export const errorText = (err, fallback = 'That did not go through') => err?.response?.data?.error || fallback;

/* ------------------------------------------------------------- the steps */

/** A step id made here, for a step added in the editor: short and url-safe. */
export const newStepId = () => {
  const a = new Uint8Array(6);
  (window.crypto || window.msCrypto).getRandomValues(a);
  return btoa(String.fromCharCode(...a)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

export const blankStep = (extra = {}) => ({
  id: newStepId(), title: '', notes: null, priority: 4, labels: [], checklist: [], section: null,
  parent: null, offset: 0, time: null, role: null, after: [], ...extra
});

/** How deep each step sits (0 at the top), by id. */
export const levelsOf = (steps) => {
  const lv = new Map();
  for (const s of steps) lv.set(s.id, s.parent && lv.has(s.parent) ? lv.get(s.parent) + 1 : 0);
  return lv;
};

/** The index just past step i's subtree (its sub-steps, at any depth). */
export const subtreeEnd = (steps, i) => {
  const lv = levelsOf(steps);
  const own = lv.get(steps[i].id);
  let j = i + 1;
  while (j < steps.length && lv.get(steps[j].id) > own) j += 1;
  return j;
};

/*
 * THE OUTLINE. The editor's moves - drag, indent, outdent - are done on each
 * step's LEVEL, as an outliner does, and the parents are then read back off
 * the levels: a step's parent is the nearest step above it one level up. A
 * level is never more than one deeper than the step above it, nor deeper
 * than 2 (a step, its sub-step, theirs - as a to-do goes), so whatever is
 * done, the outline comes out as one a to-do list can hold.
 */
const withLevels = (steps) => {
  const lv = levelsOf(steps);
  return steps.map((s) => ({ s, level: lv.get(s.id) }));
};
const fromLevels = (rows) => {
  const out = [];
  const lastAt = [];
  rows.forEach(({ s, level }, i) => {
    const prev = i ? rows[i - 1].level : -1;
    const l = Math.max(0, Math.min(level, prev + 1, 2));
    rows[i].level = l;
    lastAt[l] = s.id;
    lastAt.length = l + 1;
    const parent = l ? lastAt[l - 1] : null;
    out.push({ ...s, parent, section: parent ? null : s.section });
  });
  return out;
};

/**
 * Move step `from`, with everything under it, so that it lands at `to`
 * counted among the other steps (useReorder's count, which includes the
 * moving step's own sub-steps). It keeps its level where it can.
 */
export const moveStep = (steps, from, to) => {
  const rows = withLevels(steps);
  const end = subtreeEnd(steps, from);
  const kids = end - from - 1;
  if (to >= from && to <= from + kids) return steps;
  const block = rows.slice(from, end);
  const rest = [...rows.slice(0, from), ...rows.slice(end)];
  const at = Math.max(0, Math.min(to > from ? to - kids : to, rest.length));
  const above = at ? rest[at - 1].level : -1;
  const shift = Math.min(block[0].level, above + 1) - block[0].level;
  const moved = block.map((r) => ({ ...r, level: r.level + shift }));
  return fromLevels([...rest.slice(0, at), ...moved, ...rest.slice(at)]);
};

/** Indent step i (and what is under it) one level, under the step above. */
export const canIndent = (steps, i) => {
  if (i <= 0) return false;
  const rows = withLevels(steps);
  const end = subtreeEnd(steps, i);
  const deepest = Math.max(...rows.slice(i, end).map((r) => r.level));
  return rows[i].level <= rows[i - 1].level && deepest < 2;
};
export const indent = (steps, i) => {
  if (!canIndent(steps, i)) return steps;
  const rows = withLevels(steps);
  const end = subtreeEnd(steps, i);
  return fromLevels(rows.map((r, k) => (k >= i && k < end ? { ...r, level: r.level + 1 } : r)));
};

/** Outdent step i (and what is under it) one level. */
export const canOutdent = (steps, i) => Boolean(steps[i]?.parent);
export const outdent = (steps, i) => {
  if (!canOutdent(steps, i)) return steps;
  const rows = withLevels(steps);
  const end = subtreeEnd(steps, i);
  return fromLevels(rows.map((r, k) => (k >= i && k < end ? { ...r, level: r.level - 1 } : r)));
};

/** Take a step out, with its sub-steps, and every "after" that named them. */
export const removeStep = (steps, i) => {
  const end = subtreeEnd(steps, i);
  const gone = new Set(steps.slice(i, end).map((s) => s.id));
  return steps.filter((s) => !gone.has(s.id)).map((s) => ({ ...s, after: (s.after || []).filter((a) => !gone.has(a)) }));
};

/* ----------------------------------------------------------------- words */

const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/**
 * When a step falls, said the way a person would: "On the start date",
 * "3 working days after the start", "5 days before it is due", "No date".
 * `short` is the chip's version: "Start", "+3", "−5 wd".
 */
export const offsetLabel = (offset, { anchor = 'start', day_kind: kind = 'working' } = {}, short = false) => {
  if (offset === null || offset === undefined || offset === '') return short ? 'No date' : 'No date';
  const n = Number(offset);
  const unit = kind === 'working' ? 'working day' : 'day';
  if (short) {
    if (n === 0) return anchor === 'due' ? 'Due day' : 'Start day';
    return `${n > 0 ? '+' : '−'}${Math.abs(n)}${kind === 'working' ? ' wd' : ' d'}`;
  }
  if (n === 0) return anchor === 'due' ? 'On the due date' : 'On the start date';
  const what = plural(Math.abs(n), unit);
  if (anchor === 'due') return n < 0 ? `${what} before it is due` : `${what} after it is due`;
  return n > 0 ? `${what} after the start` : `${what} before the start`;
};

/** "Used 12 times · last 3 Oct" / "Not used yet". */
export const usageLabel = (t, now = new Date()) => {
  if (!t.use_count) return 'Not used yet';
  const last = t.last_used_at ? new Date(t.last_used_at) : null;
  const when = last ? last.toLocaleDateString('en-GB', {
    day: 'numeric', month: 'short', ...(last.getFullYear() !== now.getFullYear() ? { year: 'numeric' } : {})
  }) : null;
  return `Used ${plural(t.use_count, 'time')}${when ? ` · last ${when}` : ''}`;
};

/** A new list's name: "VAT return – Alpha Ltd – Q3 2026". */
export const suggestListName = (name, client, period) => [name, client, period]
  .map((s) => String(s || '').trim()).filter(Boolean).join(' – ').slice(0, 80);

/**
 * The person a role probably is: somebody whose name the role is (a list
 * saved as a template names its roles after people), else nobody.
 */
export const guessPerson = (role, people) => {
  const r = String(role || '').trim().toLowerCase();
  if (!r) return null;
  return (people || []).find((p) => String(p.name || '').trim().toLowerCase() === r) || null;
};

/** The template's shape as the server wants it saved. */
export const toBody = (t) => ({
  name: t.name, description: t.description || null, color: t.color || null, icon: t.icon || null,
  visibility: t.visibility, anchor: t.anchor, day_kind: t.day_kind, roles: t.roles,
  steps: t.steps.map((s) => ({
    id: s.id, title: s.title, notes: s.notes || null, priority: Number(s.priority) || 4,
    labels: s.labels || [], checklist: s.checklist || [], section: s.parent ? null : (s.section || null),
    parent: s.parent || null, offset: s.offset === '' || s.offset === null || s.offset === undefined ? null : Number(s.offset),
    time: s.offset === null || s.offset === '' ? null : (s.time || null), role: s.role || null, after: s.after || []
  }))
});
